import React, { useEffect } from 'react';
import styles from './NewBot.module.scss';
import Select from 'component/atom/forms/select/select/Select';
import Input from 'component/atom/forms/input/input/Input';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Modal from 'component/hoc/modal/Modal';
import { usePost } from 'hooks/usePost';
import { scriptEndpoints } from 'hooks/endpoints';
import TextArea from 'component/atom/forms/text-area/TextArea';
import { useQueryClient } from '@tanstack/react-query';

const Form = ({ formik, isEdit }) => {
  const typeOptions = [
    { label: 'Guest', value: 0 },
    { label: 'Authenticated ', value: 1 },
  ];
  const botTypeOptions = [
    { label: 'Internal', value: 0 },
    { label: 'External ', value: 1 },
  ];

  const isInternal = Number(formik.values.botType) === 0;

  return (
    <form
      autoComplete='off'
      onSubmit={formik.handleSubmit}
      className={styles.formCont}
    >
      <Input
        label='Bot Name'
        placeholder='Enter bot name'
        name='botName'
        value={formik.values.botName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.botName}
        touched={formik.touched.botName}
      />
      <TextArea
        placeholder='Enter Description'
        label='Description'
        name='description'
        value={formik.values.description}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.description}
        touched={formik.touched.description}
      />
      <div className={styles['select-container']}>
        <Select
          label='Bot Type'
          placeholder='Select Bot Type'
          options={botTypeOptions}
          name='botType'
          value={formik.values.botType}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.botType}
          touched={formik.touched.botType}
          disabled={isEdit}
          classname={styles.select}
          inputClassName={isEdit ? styles['disabled-select'] : undefined}
          selectClassname={isEdit ? styles['disabled-select'] : undefined}
        />
        {!isInternal && (
          <Select
            label='User Type'
            placeholder='Select type'
            options={typeOptions}
            name='type'
            value={formik.values.type}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.type}
            touched={formik.touched.type}
            disabled={isEdit || isInternal}
            classname={styles.select}
            inputClassName={isEdit ? styles['disabled-select'] : undefined}
            selectClassname={isEdit ? styles['disabled-select'] : undefined}
          />
        )}
      </div>
    </form>
  );
};

const NewBot = ({
  isOpen,
  onClose,
  setRefetch,
  selectedItem,
  setSelectedItem,
  refetch,
}) => {
  const { mutate: AddBot, isPending } = usePost();
  const queryClient = useQueryClient();
  const { mutate: EditBot, isPending: isEditing } = usePost({
    onSuccessToast: selectedItem
      ? 'Bot Details Are Updated Successfully'
      : 'Bot Added Successfully',
    toastType: 'add',
  });

  const trimToEmpty = (v) => (typeof v === 'string' ? v.trim() : v);

  const validationSchema = Yup.object({
    botName: Yup.string()
      .transform(trimToEmpty)
      .min(1, 'Bot Name is Required')
      .required('Bot Name is Required')
      .max(50, 'Bot Name cannot exceed 50 characters.'),
    description: Yup.string()
      .transform(trimToEmpty)
      .min(1, 'Description is Required')
      .required('Description is Required')
      .max(200, 'Description must not exceed 200 characters'),
    language: Yup.string().required('Language is Required'),
    botType: Yup.mixed().required('Bot type is Required'),
    type: Yup.mixed().when('botType', {
      is: (v) => Number(v) === 0,
      then: (schema) => schema.strip(),
      otherwise: (schema) => schema.required('User type is Required'),
    }),
  });

  const formik = useFormik({
    initialValues: {
      botName: selectedItem?.botName ?? '',
      type: selectedItem?.type ?? '',
      description: selectedItem?.description ?? '',
      botType: selectedItem?.botType ?? '',
      language: 'HTML',
    },
    validationSchema,
    onSubmit: (values /* , { resetForm } */) => {
      const clean = validationSchema.cast(values);

      const payload =
        Number(values.botType) === 0
          ? (() => {
              const { type, ...rest } = clean || {};
              return rest;
            })()
          : clean;

      if (!selectedItem) {
        AddBot(
          { data: payload, url: scriptEndpoints.createBot() },
          {
            onSuccess: (e) => {
              if (!e?.data?.isSuccess) return;
              onClose(); // close first to avoid flicker during animation
              refetch();
              queryClient.invalidateQueries({ queryKey: ['botsData'] });
            },
          }
        );
      } else {
        EditBot(
          {
            data: { ...payload, botId: selectedItem.botId },
            url: scriptEndpoints.editBot(),
          },
          {
            onSuccess: (e) => {
              if (!e?.data?.isSuccess) return;
              onClose(); // close first to avoid flicker during animation
              refetch();
              queryClient.invalidateQueries({ queryKey: ['botsData'] });
            },
          }
        );
      }
    },
    enableReinitialize: true,
  });

  // Re-initialize values only WHEN the modal opens (not when it closes)
  useEffect(() => {
    if (isOpen) {
      const next = {
        botName: selectedItem?.botName ?? '',
        type: selectedItem?.type ?? '',
        description: selectedItem?.description ?? '',
        botType: selectedItem?.botType ?? '',
        language: 'HTML',
      };
      formik.setValues(next, false);
      formik.setTouched({}, false);
      formik.setErrors({});
    }
  }, [isOpen, selectedItem]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Modal
        title={!selectedItem ? 'New Bot' : 'Edit Bot'}
        isOpen={isOpen}
        onClose={onClose}
        modalContent={{
          component: <Form formik={formik} isEdit={!!selectedItem} />,
        }}
        onSubmit={formik.handleSubmit}
        buttonType='submit'
        onSubmitstyles={{
          onSubmit: {
            label: !selectedItem ? 'Save' : 'Update',
            type: 'save',
            opicBAckground: true,
          },
        }}
        isLoading={isPending || isEditing}
      />
    </div>
  );
};

export default NewBot;
